import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  startCreateContentFolder,
  startListenerContentFolders,
  startMoveContentsToFolder,
  startRenameContentFolder,
  startUnassignContentsFromFolder,
  stopListenerContentFolders,
  startDeleteContentFolder,
} from "../actions/contentFolders";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import { useDispatchApp } from "../lib/redux";
import { useConfirmDeleteFolder } from "./useConfirmDeleteFolder";
import {
  ERROR,
  GALLERY_FOLDERS_MOVE_ERROR,
  GALLERY_FOLDERS_MOVE_SUCCESS,
  GALLERY_FOLDERS_UNASSIGN_ERROR,
  GALLERY_FOLDERS_UNASSIGN_SUCCESS,
} from "../i18n/keysTranslations";
import { ALL_FILTER, CONTENT_FOLDER_ROOT_ID } from "../utils/constants";
import { getFolderPathLabel } from "../utils/contentFolderUtils";
import { releaseOwnListener } from "../utils/releaseOwnListener";

export const useGalleryFolderSession = ({
  modalOpen,
  folderFilterID,
  onFolderFilterChange,
  setData,
  clearSelection,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [foldersByID, setFoldersByID] = useState({});
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isMovingContents, setIsMovingContents] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);
  const [isOpenMoveFolderModal, setIsOpenMoveFolderModal] = useState(false);
  const [isOpenRenameFolderModal, setIsOpenRenameFolderModal] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const listenerRef = useRef(null);

  const activeFolderID = folderFilterID === ALL_FILTER ? null : folderFilterID;

  useEffect(() => {
    if (!modalOpen) {
      setFoldersByID({});
      setIsLoadingFolders(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = null;
    setIsLoadingFolders(true);
    dispatch(
      startListenerContentFolders({
        onUpdatedDataCallback: (folders) => {
          if (cancelled) return;
          setFoldersByID(folders || {});
          setIsLoadingFolders(false);
        },
        onReferenceAvailable: (nextUnsubscribe) => {
          unsubscribe = nextUnsubscribe;
          listenerRef.current = nextUnsubscribe;
          if (cancelled) {
            releaseOwnListener(nextUnsubscribe);
          }
        },
      })
    ).then((started) => {
      if (typeof started === "function") {
        unsubscribe = started;
        listenerRef.current = started;
        if (cancelled) {
          releaseOwnListener(started);
          return;
        }
      }
      if (!started) setIsLoadingFolders(false);
    });

    return () => {
      cancelled = true;
      dispatch(stopListenerContentFolders({ unsubscribe }));
      listenerRef.current = null;
    };
  }, [dispatch, modalOpen]);

  const folders = useMemo(() => {
    return Object.entries(foldersByID)
      .map(([id, folder]) => ({
        id,
        ...folder,
      }))
      .sort((left, right) => right.creationTime - left.creationTime);
  }, [foldersByID]);

  const getFolderLabel = useCallback(
    ({ folderID }) => getFolderPathLabel({ folders, folderID }),
    [folders]
  );

  const stampFolderContent = useCallback(
    (content) => {
      if (content?.folderID) return content;
      return activeFolderID
        ? { ...content, folderID: activeFolderID }
        : content;
    },
    [activeFolderID]
  );

  const onCreateFolder = useCallback(
    async ({ name, parentFolderID }) => {
      setIsCreatingFolder(true);
      try {
        return await dispatch(
          startCreateContentFolder({ name, parentFolderID })
        );
      } finally {
        setIsCreatingFolder(false);
      }
    },
    [dispatch]
  );

  const applyFolderChangeToLocalData = useCallback(
    ({ contentIDs, folderID }) => {
      const movedContentIDs = new Set(contentIDs.map(String));

      setData((currentData) => {
        const updatedData = currentData.map((item) => {
          if (!movedContentIDs.has(String(item.id))) return item;

          const updatedItem = { ...item };
          if (folderID) {
            updatedItem.folderID = folderID;
          } else {
            delete updatedItem.folderID;
          }
          return updatedItem;
        });

        if (
          folderFilterID !== ALL_FILTER &&
          (folderID === null || folderFilterID !== folderID)
        ) {
          return updatedData.filter(
            (item) => !movedContentIDs.has(String(item.id))
          );
        }

        return updatedData;
      });
      clearSelection();
    },
    [clearSelection, folderFilterID, setData]
  );

  const applyMoveContents = useCallback(
    async ({ contentIDs, folderID }) => {
      if (!contentIDs?.length) {
        return { ok: false, reason: "noChanges" };
      }

      setIsMovingContents(true);
      try {
        const response = folderID
          ? await dispatch(startMoveContentsToFolder({ contentIDs, folderID }))
          : await dispatch(startUnassignContentsFromFolder({ contentIDs }));

        if (!response?.ok) {
          return response;
        }

        applyFolderChangeToLocalData({
          contentIDs: response.data.contentIDs,
          folderID: folderID || null,
        });

        return response;
      } finally {
        setIsMovingContents(false);
      }
    },
    [applyFolderChangeToLocalData, dispatch]
  );

  const onDropMany = useCallback(
    async ({ contentIDs, folderID }) => {
      if (!contentIDs?.length) return;

      const response = await applyMoveContents({ contentIDs, folderID });

      if (!response?.ok) {
        if (response?.reason === "noChanges") return;

        await SimpleAlert({
          icon: "error",
          title: t(ERROR),
          text: t(
            folderID
              ? GALLERY_FOLDERS_MOVE_ERROR
              : GALLERY_FOLDERS_UNASSIGN_ERROR
          ),
        });
        return;
      }

      await SimpleAlert({
        icon: "success",
        title: t(
          folderID
            ? GALLERY_FOLDERS_MOVE_SUCCESS
            : GALLERY_FOLDERS_UNASSIGN_SUCCESS
        ),
      });
    },
    [applyMoveContents, t]
  );

  const onRenameFolder = useCallback(
    async ({ folderID, name }) => {
      setIsRenamingFolder(true);
      try {
        return await dispatch(startRenameContentFolder({ folderID, name }));
      } finally {
        setIsRenamingFolder(false);
      }
    },
    [dispatch]
  );

  const onSelectFolder = useCallback(
    (folderID) => {
      clearSelection();
      onFolderFilterChange(folderID === null ? ALL_FILTER : folderID);
    },
    [clearSelection, onFolderFilterChange]
  );

  const onCreateFolderInContext = useCallback(
    async ({ name, parentFolderID }) =>
      onCreateFolder({
        name,
        parentFolderID: parentFolderID ?? activeFolderID,
      }),
    [activeFolderID, onCreateFolder]
  );

  const openCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(true);
  }, []);

  const closeCreateFolderModal = useCallback(() => {
    setIsOpenCreateFolderModal(false);
  }, []);

  const openMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(true);
  }, []);

  const closeMoveFolderModal = useCallback(() => {
    setIsOpenMoveFolderModal(false);
  }, []);

  const openRenameFolderModal = useCallback((folder) => {
    setRenameFolderTarget(folder);
    setIsOpenRenameFolderModal(true);
  }, []);

  const closeRenameFolderModal = useCallback(() => {
    setIsOpenRenameFolderModal(false);
    setRenameFolderTarget(null);
  }, []);

  const createFolderParentID = activeFolderID ?? CONTENT_FOLDER_ROOT_ID;
  const createFolderParentLabel = activeFolderID
    ? getFolderLabel({ folderID: activeFolderID })
    : null;

  const deleteFolder = useCallback(
    async ({ folderID }) => {
      return dispatch(startDeleteContentFolder({ folderID }));
    },
    [dispatch]
  );

  const onFolderDeleted = useCallback(
    ({ folder }) => {
      if (activeFolderID === folder.id) onSelectFolder(null);
    },
    [activeFolderID, onSelectFolder]
  );

  const onDeleteFolder = useConfirmDeleteFolder({
    deleteFolder,
    onDeleted: onFolderDeleted,
  });

  return {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    stampFolderContent,
    onSelectFolder,
    onDropMany,
    moveContents: applyMoveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  };
};
